import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Chip,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
} from '@mui/icons-material';
import { GeolocationPosition } from '@/types';
import { geolocationService } from '@/services/geolocationService';
import { notificationService } from '@/services/notificationService';
import { BackButton, Button } from './UI';

interface LocationFormProps {
  location: GeolocationPosition | undefined;
  onChange: (location: GeolocationPosition | undefined) => void;
  onSubmit: () => void;
  onBack: () => void;
  disabled: boolean;
  isSubmitting: boolean;
  showNavigationButtons?: boolean;
}

export const LocationForm: React.FC<LocationFormProps> = ({
  location,
  onChange,
  onSubmit,
  onBack,
  disabled,
  isSubmitting,
  showNavigationButtons = true,
}) => {
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [address, setAddress] = useState<string>('');
  const [locationError, setLocationError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);

  useEffect(() => {
    // Verifica se la geolocalizzazione è supportata
    if (!geolocationService.isSupported()) {
      setLocationError('Geolocalizzazione non supportata da questo browser');
    }
  }, []);

  const handleGetCurrentLocation = async () => {
    setIsGettingLocation(true);
    setLocationError(null);
    setPermissionDenied(false);

    try {
      // Avvia il monitoraggio geolocalizzazione se non è già attivo
      // Questo è necessario per rispettare le policy del browser sui user gesture
      geolocationService.startWatchingIfNeeded();
      
      // Prova prima con retry automatico
      const position = await geolocationService.getPositionWithRetry(2);

      // Ottieni indirizzo se possibile
      let addressText = '';
      try {
        addressText = await geolocationService.getAddressFromCoordinates(
          position.latitude,
          position.longitude
        );
        setAddress(addressText);
      } catch (error) {
        console.warn('Reverse geocoding failed:', error);
        addressText = `${position.latitude.toFixed(6)}, ${position.longitude.toFixed(6)}`;
        setAddress(addressText);
      }

      // Passa la posizione con l'indirizzo
      onChange({
        ...position,
        address: addressText
      });
    } catch (error: any) {
      // Log più pulito per errori comuni in sviluppo
      if (import.meta.env.MODE === 'development') {
        const errorMessage = error.message;
        if (errorMessage.includes('Location information unavailable') || 
            errorMessage.includes('Position update is unavailable') ||
            errorMessage.includes('Location request timed out')) {
          console.warn('Geolocation error:', errorMessage);
        } else {
          console.error('Geolocation error:', error);
        }
      }
      
      // Prova fallback con ultima posizione nota
      try {
        const fallbackPosition = await geolocationService.getPositionWithFallback();
        if (fallbackPosition) {
          if (import.meta.env.MODE === 'development') {
            console.log('Using fallback position');
          }
          const fallbackAddress = `${fallbackPosition.latitude.toFixed(6)}, ${fallbackPosition.longitude.toFixed(6)}`;
          setAddress(fallbackAddress);
          onChange({
            ...fallbackPosition,
            address: fallbackAddress
          });
          return;
        }
      } catch (fallbackError) {
        if (import.meta.env.MODE === 'development') {
          console.warn('Fallback position also failed:', fallbackError);
        }
      }
      
      if (error.message.includes('denied')) {
        setPermissionDenied(true);
        notificationService.locationPermissionDenied();
      } else {
        setLocationError(error.message);
        notificationService.locationUnavailable();
      }
    } finally {
      setIsGettingLocation(false);
    }
  };


  const formatCoordinates = (lat: number, lng: number): string => {
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  const formatAccuracy = (accuracy?: number): string => {
    if (!accuracy) return 'N/A';
    return accuracy < 1000 ? `${Math.round(accuracy)}m` : `${(accuracy / 1000).toFixed(1)}km`;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Rileva Posizione
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        La posizione della donazione è obbligatoria per la tracciabilità. 
        Clicca su "Rileva Posizione" per ottenere automaticamente le coordinate.
      </Typography>

      {/* Modalità sviluppo - inserimento manuale coordinate */}
      {import.meta.env.MODE === 'development' && (
        <Card sx={{ mb: 3, bgcolor: 'warning.light', border: '1px solid', borderColor: 'warning.main' }}>
          <CardContent>
            <Typography variant="subtitle2" color="warning.dark" gutterBottom>
              🛠️ Modalità Sviluppo - Inserimento Manuale Coordinate
            </Typography>
            <Typography variant="body2" color="warning.dark" sx={{ mb: 2 }}>
              In ambiente di sviluppo, puoi inserire manualmente le coordinate per testare l'applicazione.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  // Coordinate di Roma come esempio
                  const testPosition = {
                    latitude: 41.9028,
                    longitude: 12.4964,
                    accuracy: 10,
                    timestamp: Date.now(),
                    address: 'Roma, Italia'
                  };
                  onChange(testPosition);
                  setAddress('Roma, Italia');
                }}
                sx={{ borderColor: 'warning.main', color: 'warning.dark' }}
              >
                Usa Roma (Test)
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  // Coordinate di Milano come esempio
                  const testPosition = {
                    latitude: 45.4642,
                    longitude: 9.1900,
                    accuracy: 10,
                    timestamp: Date.now(),
                    address: 'Milano, Italia'
                  };
                  onChange(testPosition);
                  setAddress('Milano, Italia');
                }}
                sx={{ borderColor: 'warning.main', color: 'warning.dark' }}
              >
                Usa Milano (Test)
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {!location ? (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <LocationIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Rileva Posizione
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ottieni automaticamente la posizione corrente
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGetCurrentLocation}
                disabled={disabled || isGettingLocation || !geolocationService.isSupported()}
                loading={isGettingLocation}
                loadingText="Rilevando..."
                startIcon={<MyLocationIcon />}
                sx={{ minWidth: 180 }}
              >
                Rileva Posizione
              </Button>
            </Box>

            {locationError && (
              <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
                {locationError}
              </Alert>
            )}

            {permissionDenied && (
              <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>Permesso geolocalizzazione negato</strong>
                  <br />
                  Per abilitare la geolocalizzazione:
                  <br />
                  1. Clicca sull'icona del lucchetto nella barra degli indirizzi
                  <br />
                  2. Seleziona "Consenti" per la geolocalizzazione
                  <br />
                  3. Ricarica la pagina
                </Typography>
              </Alert>
            )}

            {!geolocationService.isSupported() && (
              <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>Errore:</strong> La geolocalizzazione non è supportata da questo browser. 
                  La posizione è obbligatoria per completare la donazione.
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <LocationIcon color="primary" />
              <Typography variant="h6">
                Posizione Rilevata
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Coordinate
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {formatCoordinates(location.latitude, location.longitude)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Precisione
                </Typography>
                <Typography variant="body1">
                  {formatAccuracy(location.accuracy)}
                </Typography>
              </Box>
            </Box>

            {address && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Indirizzo
                </Typography>
                <Typography variant="body1">
                  {address}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Chip
                label="Posizione rilevata"
                color="success"
                size="small"
                icon={<LocationIcon />}
              />
              {location.accuracy && location.accuracy < 100 && (
                <Chip
                  label="Alta precisione"
                  color="primary"
                  size="small"
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleGetCurrentLocation}
                disabled={disabled || isGettingLocation}
                loading={isGettingLocation}
                loadingText="Aggiornando..."
                startIcon={<MyLocationIcon />}
                sx={{ minWidth: 140 }}
              >
                Aggiorna Posizione
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {showNavigationButtons && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <BackButton
            onClick={onBack}
            disabled={disabled || isSubmitting}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmit}
            disabled={disabled || isSubmitting || !location}
            loading={isSubmitting}
            loadingText="Salvando..."
            sx={{ minWidth: 120 }}
          >
            Avanti
          </Button>
        </Box>
      )}
    </Box>
  );
};
